import React, { useState } from 'react';
import './SubscribeModal.css';
import { subscriptionsAPI } from '../../services/api';

export interface SubscribeModalTier {
  id: string;
  name: string;
  price: number;
  features: string[];
  isPopular?: boolean;
}

interface SubscribeModalProps {
  tier: SubscribeModalTier;
  user: { id: string; email: string; displayName: string };
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'review' | 'paying' | 'verifying' | 'done' | 'error';

const SubscribeModal: React.FC<SubscribeModalProps> = ({ tier, user, onClose, onSuccess }) => {
  const [step, setStep] = useState<Step>('review');
  const [error, setError] = useState<string | null>(null);

  const openCheckout = async () => {
    setError(null);
    setStep('paying');
    try {
      const resp = await subscriptionsAPI.create({
        planType: tier.id,
        userId: user.id,
        email: user.email,
        displayName: user.displayName,
      } as any);

      const approvalUrl = (resp as any).approvalUrl;
      if (!approvalUrl) throw new Error('No approval URL returned');

      const w = 520;
      const h = 760;
      const left = (window.screen.width / 2) - (w / 2);
      const top = (window.screen.height / 2) - (h / 2);
      const popup = window.open(
        approvalUrl,
        'oj_paypal',
        `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,location=no`
      );

      if (!popup) {
        // Popup blocked — fall back to same-window redirect
        window.location.href = approvalUrl;
        return;
      }

      const subscriptionId = (resp as any).subscriptionId as string | undefined;

      const startedAt = Date.now();
      const closedTimer = window.setInterval(async () => {
        if (popup.closed) {
          window.clearInterval(closedTimer);
          setStep('verifying');
          // Defense-in-depth: actively call activate with the subscription id we
          // got from create(). The worker only activates pending rows so this
          // is idempotent — if PHP already activated, this returns 404 silently.
          if (subscriptionId) {
            try {
              await subscriptionsAPI.activate({
                subscriptionId,
                type: 'subscription' as any,
              } as any);
            } catch { /* webhook may already have activated; fall through */ }
          }
          // Poll backend for activation up to 30s
          let activated = false;
          for (let i = 0; i < 10 && !activated; i++) {
            try {
              const my: any = await subscriptionsAPI.getMySubscription();
              if (my?.subscription?.status === 'active') {
                activated = true;
                break;
              }
            } catch {}
            await new Promise(r => setTimeout(r, 3000));
          }
          if (activated) {
            setStep('done');
            setTimeout(() => onSuccess(), 1200);
          } else {
            setStep('error');
            setError('We did not detect a completed payment. If you paid, refresh in a minute or contact support.');
          }
        } else if (Date.now() - startedAt > 15 * 60 * 1000) {
          window.clearInterval(closedTimer);
          try { popup.close(); } catch {}
          setStep('error');
          setError('Checkout timed out after 15 minutes.');
        }
      }, 1500);
    } catch (e: any) {
      setStep('error');
      setError(e?.message || 'Failed to start checkout.');
    }
  };

  return (
    <div className="oj-modal-backdrop" onClick={step === 'review' || step === 'error' ? onClose : undefined}>
      <div className="oj-modal subscribe-modal" onClick={e => e.stopPropagation()}>
        <button
          className="oj-modal-close"
          onClick={onClose}
          disabled={step === 'paying' || step === 'verifying'}
          aria-label="Close"
        >
          ×
        </button>

        {step === 'review' && (
          <>
            <div className="subscribe-modal-header">
              <span className="oj-eyebrow">Confirm subscription</span>
              <h2>{tier.name}</h2>
              <div className="subscribe-modal-price">
                <span className="amount">${tier.price}</span>
                <span className="period">
                  {tier.id === 'lifetime' ? 'one-time' :
                   tier.id === 'yearly'   ? '/year' :
                   tier.id === 'quarterly'? '/3 months' :
                                            '/month'}
                </span>
              </div>
            </div>
            <ul className="subscribe-modal-features">
              {tier.features.map((f, i) => (
                <li key={i}>
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  {f}
                </li>
              ))}
            </ul>
            <button className="subscribe-modal-cta" onClick={openCheckout}>
              Continue to PayPal
            </button>
            <p className="subscribe-modal-fineprint">
              Secure checkout opens in a small window. We never see your card.
              Cancel anytime in your profile.
            </p>
          </>
        )}

        {(step === 'paying' || step === 'verifying') && (
          <div className="subscribe-modal-progress">
            <div className="oj-spinner" />
            <h3>{step === 'paying' ? 'Complete payment in the popup' : 'Verifying payment…'}</h3>
            <p>
              {step === 'paying'
                ? 'Finish checkout in the PayPal window. We will activate access automatically when it closes.'
                : 'Hang tight — confirming with PayPal. This usually takes a few seconds.'}
            </p>
          </div>
        )}

        {step === 'done' && (
          <div className="subscribe-modal-done">
            <div className="check-icon">✓</div>
            <h3>You are in.</h3>
            <p>Welcome to the inner circle. Loading your feed…</p>
          </div>
        )}

        {step === 'error' && (
          <div className="subscribe-modal-error">
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button className="subscribe-modal-cta" onClick={openCheckout}>Try again</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribeModal;
